import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { trackAppStoreClick } from '@/lib/posthog';

const APP_STORE_URL = 'https://apps.apple.com/us/app/athlete-space/id6758096470';

const AppleIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const NavLogo = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const logoClasses = "h-8 md:h-10 lg:h-11 w-auto opacity-95 pointer-events-none drop-shadow-[0_0_12px_rgba(120,180,255,0.25)]";

  if (prefersReducedMotion || videoFailed) {
    return (
      <img
        src="/AppIcon.png"
        alt="Athlete Space"
        className={logoClasses}
        onError={(e) => {
          // Ultimate fallback: show text
          const span = document.createElement('span');
          span.textContent = 'AthleteSpace';
          span.className = 'text-slate-300 font-mono text-sm tracking-wider';
          e.currentTarget.parentNode?.replaceChild(span, e.currentTarget);
        }}
      />
    );
  }

  return (
    <video
      src="/branding/athletespace-logo-motion.mp4"
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      className={logoClasses}
      onError={() => setVideoFailed(true)}
      {...({ 'webkit-playsinline': 'true' } as React.VideoHTMLAttributes<HTMLVideoElement> & { 'webkit-playsinline'?: string })}
    />
  );
};

export const TelemetryNav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.1 }}
    >
      <div
        className="absolute inset-0 border-b border-white/10"
        style={{
          background: 'rgba(2, 6, 23, 0.85)',
          backdropFilter: 'blur(12px)',
        }}
      />
      
      <div className="relative container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <NavLogo />
          </Link>

          <div className="hidden md:flex items-center gap-8 text-[11px] font-mono tracking-[0.12em] uppercase">
            <a href="#system" className="text-slate-500 hover:text-slate-300 transition-colors">System</a>
            <a href="#athletes" className="text-slate-500 hover:text-slate-300 transition-colors">Athletes</a>
            <a href="#coaches" className="text-slate-500 hover:text-slate-300 transition-colors">Coaches</a>
            <Link to="/about" className="text-slate-500 hover:text-slate-300 transition-colors">About</Link>
            <Link to="/science" className="text-slate-500 hover:text-slate-300 transition-colors">Science & AI</Link>
            <Link to="/faq" className="text-slate-500 hover:text-slate-300 transition-colors">FAQ</Link>
            <Link to="/support" className="text-slate-500 hover:text-slate-300 transition-colors">Support</Link>
            <Link to="/login" className="text-slate-400 hover:text-slate-200 transition-colors">Sign In</Link>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppStoreClick('navbar')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-950 rounded-md text-[10px] font-mono tracking-[0.08em] uppercase hover:bg-slate-200 transition-colors"
            >
              <AppleIcon className="w-3.5 h-3.5" />
              <span>Download</span>
            </a>
          </div>

          <div className="flex items-center gap-3 md:hidden">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppStoreClick('navbar')}
              className="flex items-center justify-center w-8 h-8 bg-white text-slate-950 rounded-md hover:bg-slate-200 transition-colors"
            >
              <AppleIcon className="w-4 h-4" />
            </a>
            <button
              className="text-slate-400"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <motion.div
          className="md:hidden absolute left-0 right-0 border-b border-white/10"
          style={{
            top: 'calc(4rem + env(safe-area-inset-top, 0px))',
            background: 'rgba(2, 6, 23, 0.85)',
            backdropFilter: 'blur(12px)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="container mx-auto px-6 py-4 flex flex-col gap-4 text-[11px] font-mono tracking-[0.12em] uppercase">
            <a href="#system" className="text-slate-500 hover:text-slate-300 transition-colors py-2">System</a>
            <a href="#athletes" className="text-slate-500 hover:text-slate-300 transition-colors py-2">Athletes</a>
            <a href="#coaches" className="text-slate-500 hover:text-slate-300 transition-colors py-2">Coaches</a>
            <Link to="/about" className="text-slate-500 hover:text-slate-300 transition-colors py-2">About</Link>
            <Link to="/science" className="text-slate-500 hover:text-slate-300 transition-colors py-2">Science & AI</Link>
            <Link to="/faq" className="text-slate-500 hover:text-slate-300 transition-colors py-2">FAQ</Link>
            <Link to="/support" className="text-slate-500 hover:text-slate-300 transition-colors py-2">Support</Link>
            <Link to="/login" className="text-slate-400 hover:text-slate-200 transition-colors py-2">Sign In</Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
